import type { Probe } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';

/** canPlayType for a representative codec matrix, plus EME and device enumeration. */
const CODECS = [
  'video/mp4; codecs="avc1.42E01E"',
  'video/mp4; codecs="avc1.640028"',
  'video/mp4; codecs="hev1.1.6.L93.B0"',
  'video/webm; codecs="vp8"',
  'video/webm; codecs="vp9"',
  'video/webm; codecs="av01.0.05M.08"',
  'video/mp4; codecs="av01.0.05M.08"',
  'audio/mpeg',
  'audio/ogg; codecs="vorbis"',
  'audio/ogg; codecs="opus"',
  'audio/wav; codecs="1"',
  'audio/webm; codecs="opus"',
  'audio/flac',
];

const DRM_SYSTEMS = [
  'com.widevine.alpha',
  'com.microsoft.playready',
  'com.apple.fps',
];

export const mediaProbe: Probe = {
  id: 'media',
  label: 'Media & DRM',
  layer: 'media',
  tier: 'client',
  category: 'browser',
  description: 'Codec support, MediaSource, and EME key-system access',
  budgetMs: 4000,

  async run() {
    const video = document.createElement('video');
    const audio = document.createElement('audio');

    const canPlay: Record<string, string> = {};
    for (const c of CODECS) {
      const el = c.startsWith('audio') ? audio : video;
      try {
        canPlay[c] = el.canPlayType(c);
      } catch {
        canPlay[c] = '';
      }
    }

    // MediaSource.isTypeSupported — differs subtly from canPlayType (MSE has a
    // stricter container/codec matrix), so it is a genuinely distinct signal.
    const mseSupported: Record<string, boolean> = {};
    if (typeof MediaSource !== 'undefined') {
      for (const c of CODECS) {
        try {
          mseSupported[c] = MediaSource.isTypeSupported(c);
        } catch {
          mseSupported[c] = false;
        }
      }
    }

    // Device enumeration. Labels/groups are empty without permission, but the
    // *counts* and kinds are still a device-shape signal.
    let devices: { audioinput: number; audiooutput: number; videoinput: number } | null = null;
    let devicesStatus: 'ok' | 'unavailable' = 'ok';
    if (navigator.mediaDevices?.enumerateDevices) {
      try {
        const list = await navigator.mediaDevices.enumerateDevices();
        devices = {
          audioinput: list.filter((d) => d.kind === 'audioinput').length,
          audiooutput: list.filter((d) => d.kind === 'audiooutput').length,
          videoinput: list.filter((d) => d.kind === 'videoinput').length,
        };
      } catch {
        devicesStatus = 'unavailable';
      }
    }

    // EME: probe key-system access. We do NOT attempt playback.
    const eme: Record<string, string> = {};
    if (navigator.requestMediaKeySystemAccess) {
      for (const ks of DRM_SYSTEMS) {
        try {
          await navigator.requestMediaKeySystemAccess(ks, [
            {
              initDataTypes: ['cenc'],
              videoCapabilities: [{ contentType: 'video/mp4; codecs="avc1.42E01E"' }],
            },
          ]);
          eme[ks] = 'available';
        } catch (err) {
          const name = (err as { name?: string })?.name ?? 'Error';
          eme[ks] = name === 'NotSupportedError' ? 'not-supported' : `denied:${name}`;
        }
      }
    }

    // canPlayType disagreement between MSE and the element API is normal, but a
    // codec supported by neither despite claiming a capable UA is notable — we
    // surface the raw matrix and let the coherence/other probes reason.
    const anyCodec = Object.values(canPlay).some((v) => v === 'probably' || v === 'maybe');

    return {
      signals: {
        canPlayType: ok(canPlay),
        mseSupported: ok(mseSupported),
        anyCodecSupported: ok(anyCodec, 'derived'),
        deviceCounts: devices ? ok(devices) : na('unavailable', 'client', 'enumerateDevices failed'),
        deviceStatus: ok(devicesStatus, 'client'),
        emeKeySystems: ok(eme),
      },
      parts: {
        media: {
          // Hash the support booleans, not the raw 'probably'/'maybe' strings,
          // which are implementation detail rather than a stable signal.
          mse: mseSupported,
          eme,
          deviceCounts: devices,
        },
      },
    };
  },
};

registerProbe(mediaProbe);
